/**
 * Plant provisioning — typed wrapper over POST /v1/twin/plants/provision.
 *
 * The Plant Builder generates a complete plant client-side (one generatePlant()
 * over the archetype catalog) and posts the resulting spec here; the server
 * validates + writes it to the active org in one tx — entities + connections +
 * one synthetic gateway per equipment + a tag-per-datapoint + stream bindings,
 * all stamped with a fresh plant_id. Foundry-pack + member-role gated server-side.
 * In a real org (data_mode 'real', 2026-10-26) the server writes the entities,
 * the connections and the BLE probes only, ignores the posted gateways, and
 * answers with a data_note saying where the plant's data will come from.
 */

import { api } from "@/lib/api-client";
import type { PlantEdge, PlantNode } from "@foundry/foundry-plant";
import type { GatewayPlan } from "@/lib/plant/catalog/types";

/** The posted spec (snake_case archetype_id to match the backend contract). */
export interface ProvisionPlantRequest {
  archetype_id: string;
  nodes: PlantNode[];
  edges: PlantEdge[];
  gateways: GatewayPlan[];
  /**
   * Opt this freshly-provisioned plant into the new genealogy model
   * (material-genealogy-real): the server stamps plants.source="newmodel", which
   * routes its heats through the mass-conserving / time-stamped / multi-line
   * emitter and flips `useNewModel()` on for that org. Plain optional field —
   * normally an org is switched onto the new model AFTER provisioning via the
   * platform console's per-org genealogy-model toggle (platformAdminApi
   * .setOrgGenealogyModel), not at provision time.
   */
  model?: "newmodel";
  /**
   * Mark the freshly-provisioned plant for the sim-driver: "live" walks its FSMs
   * over the broker (the plant "comes alive"); "batch" pushes periodic imported
   * heats; "both" does both. Omitted = a normal, un-driven plant. The server
   * REFUSES a non-none sim_mode on a real org (409 sim_mode_forbidden_on_real),
   * so this is only offered for demo/rehearsal orgs. See PlantBuilder.
   */
  sim_mode?: "live" | "batch" | "both";
}

/** What a successful provision created (201) — drives the success toast. */
export interface ProvisionResult {
  plant_id: string;
  org_id: string;
  site_id: string;
  archetype_id: string;
  entities: number;
  connections: number;
  gateways: number;
  tags: number;
  bindings: number;
  /** BLE handheld probes provisioned (their tags sit on the plant's manual-entry gateway). */
  probes?: number;
  /** Set in a real org only, where no gateway or tag was made: where the plant's data will come from. */
  data_note?: string;
}

/**
 * The active org's provisioned plant archetype — GET /v1/twin/plant.
 *
 * `archetype_id` is one of the foundry archetype catalog ids (e.g.
 * "iron-green-sand-auto", "al-hpdc"); empty when the plant was provisioned
 * without one or the org has no plants row (`provisioned: false`). The caller
 * maps the id → a display label via the archetype catalog and falls back to a
 * sensible default when it's empty (e.g. seeded grey-iron / pre-builder orgs).
 */
export interface PlantInfo {
  plant_id: string;
  archetype_id: string;
  source: string;
  provisioned: boolean;
  /**
   * Which genealogy model this plant runs on (material-genealogy-real C1).
   * Optional + additive — absent ⇒ treat as `"legacy"` (today's emitter +
   * UI). `"genealogy_v2"` opts the org into the mass-conserving, time-stamped,
   * per-part-traceable new model + every new affordance gated behind it.
   * This is the SINGLE backend signal `useNewModel()` (lib/plant/model-gate)
   * reads — do not branch on `source` directly elsewhere.
   */
  model_version?: "legacy" | "genealogy_v2";
  /**
   * The plant's sim-driver switch (migration 0139): none | live | batch | both.
   * "live" walks the FSMs over the broker (witnessed equipment_state); "batch"
   * pushes periodic imported heats; "none" leaves it idle. The SIM/upload agent
   * studio's "run over the wire" control reads + sets this. Absent ⇒ "none".
   */
  sim_mode?: "none" | "live" | "batch" | "both";
}

/**
 * What `POST /v1/twin/plant/clear` removed (200) — counts so the toast can
 * report them. The clear wipes the twin MODEL (entities + connections +
 * bindings) AND the derived heats/genealogy/correlations, but keeps the org,
 * its things/gateways, and dashboards intact.
 */
export interface ClearPlantResult {
  entities: number;
  connections: number;
  heats: number;
}

export const twinPlants = {
  /** Provision a generated plant into the active org (201). */
  provision: (req: ProvisionPlantRequest) =>
    api.post<ProvisionResult>("/v1/twin/plants/provision", req),
  /** The active org's provisioned plant archetype (200; never 404). */
  getActive: () => api.get<PlantInfo>("/v1/twin/plant"),
  /**
   * Set the active org's plant sim_mode — POST /v1/twin/plant/sim-mode/{mode}.
   * model.write; the server refuses a real org (409 sim_mode_forbidden_on_real)
   * and requires a provisioned plant (409 no_provisioned_plant). "live" makes the
   * sim-driver drive the plant over the broker so its equipment walks.
   */
  setSimMode: (mode: "none" | "live" | "batch" | "both") =>
    api.post<{ org_id: string; sim_mode: string }>(`/v1/twin/plant/sim-mode/${mode}`, {}),
  /**
   * Clear the twin model for the active org — `POST /v1/twin/plant/clear`.
   * Removes entities + connections + bindings + heats/genealogy/correlations;
   * KEEPS the org, things/gateways and dashboards. 200 → counts removed.
   * Destructive — the caller confirms first and invalidates the twin queries.
   */
  clear: () => api.post<ClearPlantResult>("/v1/twin/plant/clear", {}),
};
